import axios, { AxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import type { ContinuousScanResponse } from '../types';

export interface ScanUploadArgs {
  imageBlob: Blob;
  qrText: string;
  clientIdempotencyKey: string;
  modelKeys: string[];
  promptVersion?: string;
  countPromptVersion?: string;
  deviceUserAgent?: string;
  signal: AbortSignal;
}

export type ScanUploadResult =
  | { kind: 'ok'; response: ContinuousScanResponse }
  | { kind: 'qr_not_detected'; message: string }
  | { kind: 'item_not_found'; message: string }
  | { kind: 'validation_error'; message: string }
  | { kind: 'rate_limited'; message: string; retryAfterMs?: number }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'aborted' }
  | { kind: 'error'; message: string };

export async function uploadScanFrame(args: ScanUploadArgs): Promise<ScanUploadResult> {
  const form = new FormData();
  form.append('image', args.imageBlob, 'scan-frame.jpg');
  form.append('models', args.modelKeys.join(','));
  form.append('client_track', 'react-pwa');
  form.append('capture_mode', 'continuous_scan');
  form.append('client_idempotency_key', args.clientIdempotencyKey);
  form.append('client_qr_text', args.qrText);
  // client_qr_bbox_hint_json intentionally omitted: the uploaded image
  // is already a per-QR crop, so the bbox-in-full-frame coords don't
  // apply. Server-side, the only reader is the LLM bin-localizer, which
  // skips for capture_mode=continuous_scan anyway.
  form.append('auto_seed_item', 'true');
  if (args.promptVersion) form.append('prompt_version', args.promptVersion);
  if (args.countPromptVersion) form.append('count_prompt_version', args.countPromptVersion);
  if (args.deviceUserAgent) form.append('device_user_agent', args.deviceUserAgent);

  try {
    const res = await apiClient.post<{ data: ContinuousScanResponse }>(
      '/api/v1/inferences',
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: args.signal,
      }
    );
    return { kind: 'ok', response: res.data.data };
  } catch (err: unknown) {
    if (axios.isCancel(err) || (err as AbortError)?.name === 'CanceledError') {
      return { kind: 'aborted' };
    }
    const axiosErr = err as AxiosError<{ message?: string }>;
    const status = axiosErr.response?.status;
    const message = axiosErr.response?.data?.message ?? axiosErr.message ?? 'submission failed';
    if (status === 401) return { kind: 'unauthorized', message };
    if (status === 404) return { kind: 'item_not_found', message };
    if (status === 422) return { kind: 'qr_not_detected', message };
    if (status === 400) return { kind: 'validation_error', message };
    if (status === 429) {
      const retryAfter = parseRetryAfter(axiosErr.response?.headers?.['retry-after']);
      return { kind: 'rate_limited', message, retryAfterMs: retryAfter };
    }
    return { kind: 'error', message };
  }
}

interface AbortError {
  name: string;
}

function parseRetryAfter(raw: unknown): number | undefined {
  if (typeof raw !== 'string') return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds * 1000);
  const dateMs = Date.parse(raw);
  if (Number.isFinite(dateMs)) return Math.max(0, dateMs - Date.now());
  return undefined;
}
